import exifr from "exifr";

const PROGRAM = {
  en: ["Not defined", "Manual", "Program AE", "Aperture priority", "Shutter priority", "Creative program", "Action program", "Portrait mode", "Landscape mode"],
  es: ["No definido", "Manual", "Programa AE", "Prioridad de apertura", "Prioridad de velocidad", "Programa creativo", "Programa de acción", "Modo retrato", "Modo paisaje"],
};
const METERING = {
  en: { 0: "Unknown", 1: "Average", 2: "Center-weighted", 3: "Spot", 4: "Multi-spot", 5: "Evaluative / Matrix", 6: "Partial", 255: "Other" },
  es: { 0: "Desconocida", 1: "Promedio", 2: "Ponderada al centro", 3: "Puntual", 4: "Multipunto", 5: "Evaluativa / Matricial", 6: "Parcial", 255: "Otra" },
};

const round1 = (n) => Math.round(n * 10) / 10;
const toNum = (v) => (Array.isArray(v) ? (v.length === 2 && v[1] ? v[0] / v[1] : v[0]) : v);

export function formatShutter(t) {
  if (!t) return null;
  if (t >= 1) return `${round1(t)}s`;
  return `1/${Math.round(1 / t)}`;
}

export async function readExif(file) {
  const raw = await exifr.parse(file, { translateValues: false, reviveValues: true });
  if (!raw) return null;
  const d = { ...raw };
  ["FNumber", "ExposureTime", "FocalLength", "MaxApertureValue", "ISO"].forEach((k) => {
    if (d[k] != null) d[k] = toNum(d[k]);
  });
  const hasData = d.Model || d.FNumber || d.ExposureTime || d.ISO || d.FocalLength;
  return hasData ? d : null;
}

export function describeExif(d, lang) {
  const es = lang === "es";
  const make = (d.Make || "").trim();
  const model = (d.Model || "").trim();
  const camera = model ? (model.toLowerCase().startsWith(make.toLowerCase()) ? model : `${make} ${model}`.trim()) : null;
  let flash = null;
  if (typeof d.Flash === "number") {
    if (d.Flash & 0x20) flash = es ? "Sin flash" : "No flash function";
    else flash = d.Flash & 1 ? (es ? "Disparado" : "Fired") : (es ? "No disparado" : "Did not fire");
  }
  const wb = d.WhiteBalance === 0 ? (es ? "Automático" : "Auto") : d.WhiteBalance === 1 ? "Manual" : null;
  const focal = d.FocalLength ? `${round1(d.FocalLength)}mm${d.FocalLengthIn35mmFormat ? ` (${d.FocalLengthIn35mmFormat}mm ${es ? "equiv." : "equiv."})` : ""}` : null;
  const date = d.DateTimeOriginal || d.CreateDate;
  return [
    { key: "camera", label: es ? "Cámara" : "Camera", value: camera },
    { key: "lens", label: es ? "Lente" : "Lens", value: d.LensModel || null },
    { key: "aperture", label: es ? "Apertura" : "Aperture", value: d.FNumber ? `f/${round1(d.FNumber)}` : null },
    { key: "shutter", label: es ? "Velocidad" : "Shutter speed", value: formatShutter(d.ExposureTime) },
    { key: "iso", label: "ISO", value: d.ISO ? String(Array.isArray(d.ISO) ? d.ISO[0] : d.ISO) : null },
    { key: "focal", label: es ? "Distancia focal" : "Focal length", value: focal },
    { key: "mode", label: es ? "Modo de exposición" : "Exposure mode", value: typeof d.ExposureProgram === "number" ? (PROGRAM[lang] || PROGRAM.en)[d.ExposureProgram] || null : null },
    { key: "metering", label: es ? "Medición" : "Metering", value: typeof d.MeteringMode === "number" ? (METERING[lang] || METERING.en)[d.MeteringMode] || null : null },
    { key: "flash", label: "Flash", value: flash },
    { key: "wb", label: es ? "Balance de blancos" : "White balance", value: wb },
    { key: "date", label: es ? "Fecha" : "Date", value: date instanceof Date && !isNaN(date) ? date.toLocaleString(es ? "es" : "en") : date ? String(date) : null },
  ];
}

const MAKE_WORDS = /(canon|eos|nikon|corporation|sony|fujifilm|fuji|pentax|ricoh|imaging|company|ltd)/g;
const ROMAN = ["", "", "ii", "iii", "iv", "v"];
const norm = (s) => (s || "").toLowerCase().replace(MAKE_WORDS, "").replace(/[^a-z0-9]/g, "");

function sonyAlias(n) {
  let m = n.match(/^ilce(\d{4})$/);
  if (m) return `a${m[1]}`;
  m = n.match(/^ilce(7[rsc]?|9)m(\d)$/);
  if (m) return `a${m[1]}${ROMAN[parseInt(m[2])] || ""}`;
  m = n.match(/^ilce(7[rsc]?|9|1)$/);
  if (m) return `a${m[1]}`;
  m = n.match(/^ilme(fx\d+)$/);
  if (m) return m[1];
  return n;
}

export function matchCameraId(model, cameras) {
  const target = sonyAlias(norm(model));
  if (!target) return null;
  const parts = cameras.map((c) => ({ id: c.id, names: c.name.split("/").map(norm) }));
  const exact = parts.find((p) => p.names.includes(target));
  if (exact) return exact.id;
  const prefix = parts.filter((p) => p.names.some((n) => n.startsWith(target)));
  return prefix.length === 1 ? prefix[0].id : null;
}

export function lensFromExif(d) {
  const fromModel = (d.LensModel || "").match(/F\s?(\d+(?:\.\d+)?)/i);
  const maxAp = d.MaxApertureValue
    ? round1(Math.pow(2, d.MaxApertureValue / 2))
    : fromModel
    ? parseFloat(fromModel[1])
    : d.FNumber
    ? round1(d.FNumber)
    : null;
  return { focal_length: d.FocalLength ? Math.round(d.FocalLength) : null, max_aperture: maxAp };
}
